import { t } from '../../common/i18n';
import { DeliveryType } from '../../generated/api/deliveryType';
import type { DeviceChangeOrRedeemRequest } from '../../common/types/device';
import type { AddonProductOfferingWithSelection } from '../../common/types/productOfferings.js';
import type { PurposeOfUseOrContact } from '../../common/types/subscription';
import type { CampaignContext } from '../../generated/api/campaignContext';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion';
import type { ChildOrderItem } from '../../generated/api/childOrderItem';
import type { Contact } from '../../generated/api/contact';
import type { DeliveryAddress } from '../../generated/api/deliveryAddress';
import type { Order } from '../../generated/api/order';
import type { OrderItemUnit } from '../../generated/api/orderItemUnit';
import type { PickupPoint } from '../../generated/api/pickupPoint';

export enum PlaceOrderStep {
  CUSTOMER = 'customer',
  PRODUCTS = 'products',
  DELIVERY = 'delivery',
  PAYMENT = 'payment',
}

export enum DeliveryModeType {
  ADDRESS = 'ADDRESS',
  COLLECT_POINT = 'COLLECT_POINT',
}

export enum PickupService {
  POSTI = 'posti',
  MATKAHUOLTO = 'matkahuolto',
}

export type DeliveryLogo = 'posti' | 'matkahuolto';

export const COLLECT_POINT_TYPES = [DeliveryType.POSTI_SMARTPOST, DeliveryType.MATKAHUOLTO_LAHELLA_PAKETTI];

export const STEP_ORDER = [
  PlaceOrderStep.CUSTOMER,
  PlaceOrderStep.PRODUCTS,
  PlaceOrderStep.DELIVERY,
  PlaceOrderStep.PAYMENT,
] as const;

export type OrderItemPurposeOrContact = PurposeOfUseOrContact & {
  basketItemId?: string;
  basketItemIndex?: number;
  additionalParameters?: Record<string, string>;
};

export type PlaceOrderItemUnit = OrderItemUnit & {
  basketItemId: string;
  basketItemIndex: number;
  addons: ChildOrderItem[];
  productOfferAddons?: AddonProductOfferingWithSelection[];
  addonRuntimeCharacteristics?: Record<string, Record<string, string>>;
  enrollmentProgram?: string;
  enrollmentProgramAlias?: string;
  deviceChange?: DeviceChangeOrRedeemRequest;
};

export enum DeliveryPlace {
  COMPANY_ADDRESS = 'companyAddress',
  OTHER_ADDRESS = 'otherAddress',
  HOME_ADDRESS = 'homeAddress',
}

export interface PlaceOrderFormData {
  products: {
    placeOrderItemUnits: Array<PlaceOrderItemUnit>;
    preferredInstallationDate?: string;
    installationAppointmentTimeSlotIds?: Record<string, string>;
  };

  delivery: {
    deliveryPlace?: DeliveryPlace;
    deliveryAddress?: DeliveryAddress;
    deliveryType?: DeliveryType;
    shipmentType?: Order.ShipmentTypeEnum;
    pickupPointPostalCode?: string;
    pickupPoint?: PickupPoint;
    deliveryPriceInCents?: number;
    deliveryAddressId?: string;
  };

  payment: {
    billingAccountId?: string;
    paymentType?: Order.PaymentTypeEnum;
  };
}

export const emptyAddressDefaults = (companyName = ''): DeliveryAddress => ({
  companyName: companyName,
  recipient: '',
  phoneNumber: '',
  address: {
    line1: '',
    line2: '',
    postalCode: '',
    postOffice: '',
    countryCode: 'FIN',
  },
});

export interface PlaceOrderStepState {
  completed: Set<PlaceOrderStep>;
}

export interface DeliveryMode {
  type: DeliveryModeType;
  icon: string;
  name: string;
}

export type DeliveryMethodEntry = {
  deliveryTypeCode: DeliveryType;
  logo: DeliveryLogo;
  header: string;
  description?: string;
  mode: DeliveryModeType;
  service?: PickupService;
};

export const getDeliveryModes = (): DeliveryMode[] => [
  {
    type: DeliveryModeType.ADDRESS,
    icon: 'delivery',
    name: t.ZQ4R('Delivery to address'),
  },
  {
    type: DeliveryModeType.COLLECT_POINT,
    icon: 'location',
    name: t.TWHW('Collect point'),
  },
];

export const getDeliveryMethods = (): DeliveryMethodEntry[] => [
  // Address delivery methods
  {
    deliveryTypeCode: DeliveryType.POSTI_EXPRESS_BUSINESS,
    logo: 'posti',
    mode: DeliveryModeType.ADDRESS,
    header: t.U992('Delivery to the delivery address by Posti'),
    description: t.RSW7('Delivery on the next business day by 4:00 PM, covering almost all of Finland.'),
  },
  {
    deliveryTypeCode: DeliveryType.POSTI_EXPRESS_MORNING,
    logo: 'posti',
    mode: DeliveryModeType.ADDRESS,
    header: t.IDD3('Delivery to the delivery address by Posti, by morning'),
    description: t.ZU4Z('Delivery on the next business day by 9:00 AM, covering almost all of Finland.'),
  },
  {
    deliveryTypeCode: DeliveryType.MATKAHUOLTO_JAKOPAKETTI,
    logo: 'matkahuolto',
    mode: DeliveryModeType.ADDRESS,
    header: t.PH3D('Delivery to the delivery address by Matkahuolto'),
    description: t.QQH4('Delivery within 1–3 business days.'),
  },
  // Collect point methods
  {
    deliveryTypeCode: DeliveryType.POSTI_SMARTPOST,
    logo: 'posti',
    mode: DeliveryModeType.COLLECT_POINT,
    service: PickupService.POSTI,
    header: t.X8GA('Posti parcel locker or pickup point'),
    description: t.RTZM('Delivery within 1–2 business days to your selected parcel locker or pickup point.'),
  },
  {
    deliveryTypeCode: DeliveryType.MATKAHUOLTO_LAHELLA_PAKETTI,
    logo: 'matkahuolto',
    mode: DeliveryModeType.COLLECT_POINT,
    service: PickupService.MATKAHUOLTO,
    header: t.PVER('Matkahuolto parcel locker or pickup point'),
    description: t.GAPN('Delivery within 1–3 business days to your selected parcel locker or pickup point.'),
  },
];

export interface ShoppingBasketUpdateMethods {
  changeQuantity: (basketItemId: string, quantity: number) => void;
  updateAddons: (
    basketItemId: string,
    productIndex: number,
    addons: string[],
    addOnAttributes?: { [addOnCode: string]: { [key: string]: string } }
  ) => void;
}

export interface ContactsUpdateMethods {
  contacts: Contact[];
  onContactCreated: () => Promise<void>;
}

export interface CampaignData {
  voucherCode: string;
  campaignsFromVoucher: CampaignPromotion[];
  campaignContextsFromVoucher: CampaignContext[];
}

export interface CustomerPrimaryAddress {
  deliveryAddress: DeliveryAddress;
  addressId: string;
}
